import React from 'react'
import RoomConditionWithBadge from '../components/RoomConditionWithBadge';
import RoomStatusColor from '../components/RoomStatusColor';
import TextWithIconIfYes from './TextWithIconIfYes';
import EditableTextWithTooltip from './EditableTextWithTooltip';
import ReadOnlyGuard from '../../../shared/components/ReadOnlyGuard';
import { useAuth } from '../../../auth/context/AuthProvider';
import { HousekeepingV4ResponseType } from '../../../services/housekeeping/response/IndexHousekeepingV4Response';
import useIndexHousekeepingV4 from '../../../services/housekeeping/hooks/useIndexHousekeepingV4';

type RoomTypeGroup = {
    room_type_id: string;
    room_type_name: string;
    child: HousekeepingV4ResponseType[];
};

function groupByRoomTypeWithName(data: HousekeepingV4ResponseType[]): RoomTypeGroup[] {
    const map = new Map<string, RoomTypeGroup>();

    data.forEach((item) => {
        const id = item.room_type_id;
        const name = item.room_type_name || 'Unknown';

        if (!map.has(id)) {
            map.set(id, {
                room_type_id: id,
                room_type_name: name,
                child: [],
            });
        }

        map.get(id)?.child.push(item);
    });

    return Array.from(map.values());
}

const HousekeepingTable = () => {
    const { user } = useAuth();

    const { data: housekeepingData, isFetching } = useIndexHousekeepingV4({
        filters: {
            property_id: user?.active_property_id
        },
    });

    if (isFetching) {
        return (
            <div className="text-center">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    const mappedData = groupByRoomTypeWithName(housekeepingData?.data || []);

    return (
        <>
            {/* Tampilan Tabel untuk Desktop */}
            <div className="d-none d-md-block table-responsive">
                <table className="table table-hover table-vcenter">
                    <thead>
                        <tr>
                            <th>Room Name</th>
                            <th>Room Condition</th>
                            <th>Room Status</th>
                            <th>Checkin Today</th>
                            <th>Arrival Date</th>
                            <th>Departure Date</th>
                            <th>Frontdesk Status</th>
                            <th>User</th>
                            <th className="text-end">Comment</th>
                        </tr>
                    </thead>
                    <tbody>
                        {mappedData.map((housekeeping, index) => (
                            <React.Fragment key={index}>
                                <tr>
                                    <td colSpan={9} className="text-center text-primary">
                                        {housekeeping.room_type_name}
                                    </td>
                                </tr>
                                {housekeeping.child.length > 0 && housekeeping.child.map((room, index) => (
                                    <tr key={index}>
                                        <td>{room.room_number}</td>
                                        <td className='text-center'>
                                            <div className='d-flex flex-row align-items-center justify-content-center'>
                                                <ReadOnlyGuard>
                                                    <RoomConditionWithBadge room_condition={room.room_condition ?? ''} id={room.id ?? ''} />
                                                </ReadOnlyGuard>
                                            </div>
                                        </td>
                                        <td>
                                            <RoomStatusColor status={room.room_status} />
                                        </td>
                                        <td>
                                            <TextWithIconIfYes isCheckinToday={room.checkin_today} label="YES" />
                                        </td>
                                        <td>{room.arrival_date || '-'}</td>
                                        <td>{room.departure_date || '-'}</td>
                                        <td>
                                            {room.frontdesk_status || '-'}
                                        </td>
                                        <td>{room.staff_name || '-'}</td>
                                        <td className="text-end">
                                            <ReadOnlyGuard>
                                                <EditableTextWithTooltip initialText={room.comment ?? ''} id={room.id ?? ''} />
                                            </ReadOnlyGuard>
                                        </td>
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Tampilan Card untuk Mobile */}
            <div className="d-md-none">
                {mappedData.map((housekeeping, index) => (
                    <div key={index} className="mb-4">
                        {/* Mengubah ukuran heading menjadi lebih besar dengan h4 */}
                        <h4 className="text-center text-primary mb-3 fs-2">
                            {housekeeping.room_type_name}
                        </h4>
                        {housekeeping.child.length > 0 && housekeeping.child.map((room, index) => (
                            <div key={index} className="card mb-3">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="card-title mb-0">Room: {room.room_number}</h5>
                                        <RoomStatusColor status={room.room_status} />
                                    </div>

                                    <div className="row g-2">
                                        <div className="col-12 d-flex justify-content-between align-items-center">
                                            <strong>Condition:</strong>
                                            <div className="ms-2">
                                                <ReadOnlyGuard>
                                                    <RoomConditionWithBadge room_condition={room.room_condition ?? ''} id={room.id ?? ''} />
                                                </ReadOnlyGuard>
                                            </div>
                                        </div>
                                        <div className="col-12 d-flex justify-content-between align-items-center">
                                            <strong>Checkin Today:</strong>
                                            <TextWithIconIfYes isCheckinToday={room.checkin_today} label="YES" />
                                        </div>
                                        <div className="col-12 d-flex justify-content-between align-items-center">
                                            <strong>Arrival Date:</strong>
                                            <span>{room.arrival_date || '-'}</span>
                                        </div>
                                        <div className="col-12 d-flex justify-content-between align-items-center">
                                            <strong>Departure Date:</strong>
                                            <span>{room.departure_date || '-'}</span>
                                        </div>
                                        <div className="col-12 d-flex justify-content-between align-items-center">
                                            <strong>Frontdesk Status:</strong>
                                            <span>{room.frontdesk_status || '-'}</span>
                                        </div>
                                        <div className="col-12 d-flex justify-content-between align-items-center">
                                            <strong>User:</strong>
                                            <span>{room.staff_name || '-'}</span>
                                        </div>
                                        <div className="col-12 d-flex justify-content-between align-items-center">
                                            <strong>Comment:</strong>
                                            <ReadOnlyGuard>
                                                <EditableTextWithTooltip initialText={room.comment ?? ''} id={room.id ?? ''} />
                                            </ReadOnlyGuard>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </>
    )
}

export default HousekeepingTable